import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment-timezone'
import { Formik } from 'formik'
import { Button, useErrorService, useUserService } from '@abroad/components'
import API from '../../utils/API'
import { ScheduleDatePicker, ScheduleTimeSlots } from './index'
import InputField from '../form/InputField'
import DeleteIcon from '../assetsComponents/common/DeleteIcon'
import {
  getTimezoneOffsetMinutes,
  getSortedTimezones,
} from '../../utils/timezone'

export const RESCHEDULE_STEPS = {
  SELECTION: 'SELECTION',
  DETAILS: 'DETAILS',
}

const ReschedulingView = ({
  booking,
  coachId,
  rescheduleStep,
  rescheduleSelection,
  onBackToList,
  onDateChange,
  onSlotSelect,
  onSelectionComplete,
  onEditTime,
  onSubmit,
  isSubmitting,
  rescheduleForm,
  onRescheduleFieldChange,
  guestList,
  onAddGuest,
  onRemoveGuest,
  onGuestUpdate,
  timezone,
  isCheckingAvailability = false,
}) => {
  const Error = useErrorService()
  const selectedDateFromProps = rescheduleSelection?.date
  const { user } = useUserService()
  const [currentMonth, setCurrentMonth] = useState(() => {
    // If condition is 'on_or_before', use current month
    if (booking?.schedulingCondition?.condition === 'on_or_before') {
      return moment().startOf('month')
    }
    if (selectedDateFromProps) {
      return moment(selectedDateFromProps).startOf('month')
    }
    if (booking?.startDateTime) {
      return moment(booking.startDateTime).startOf('month')
    }
    return moment().startOf('month')
  })
  const [availableDays, setAvailableDays] = useState({})
  const [timeSlots, setTimeSlots] = useState([])
  const [isSlotsLoading, setIsSlotsLoading] = useState(false)
  const lastLoadedMonthKeyRef = useRef(null)

  const { formerDateLabel, formerTimeLabel } = useMemo(() => {
    if (!booking?.startDateTime) {
      return { formerDateLabel: '-', formerTimeLabel: '-' }
    }

    try {
      const date = new Date(booking.startDateTime)

      if (isNaN(date.getTime())) {
        return { formerDateLabel: '-', formerTimeLabel: '-' }
      }

      // Use timezone from props (coach's selected timezone), fallback to booking timezone or browser
      const timezoneToUse =
        timezone ||
        booking?.timezone ||
        booking?.timeZone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone

      const dateOptions = {
        timeZone: timezoneToUse,
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }

      const timeOptions = {
        timeZone: timezoneToUse,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }

      const dateFormatter = new Intl.DateTimeFormat('en-US', dateOptions)
      const timeFormatter = new Intl.DateTimeFormat('en-US', timeOptions)

      return {
        formerDateLabel: dateFormatter.format(date),
        formerTimeLabel: timeFormatter.format(date),
      }
    } catch (error) {
      console.error('Error formatting former time:', error)
      return { formerDateLabel: '-', formerTimeLabel: '-' }
    }
  }, [booking, timezone])

  // Get original booking date for rescheduling (should be selected by default)
  const originalBookingDateISO = useMemo(() => {
    if (!booking?.startDateTime) {
      return null
    }

    try {
      const date = new Date(booking.startDateTime)
      if (isNaN(date.getTime())) {
        return null
      }

      // Use timezone from props (coach's selected timezone), fallback to booking timezone or browser
      const timezoneToUse =
        timezone ||
        booking?.timezone ||
        booking?.timeZone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone

      // Convert to YYYY-MM-DD in the appropriate timezone
      const dateStr = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezoneToUse,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date)

      return dateStr
    } catch (error) {
      console.error('Error getting original booking date:', error)
      return null
    }
  }, [booking, timezone])

  const selectedDateISO = rescheduleSelection?.date

  const bookingDuration = booking?.duration
  const bookingSowId = booking?.sowId
  const eventId = booking?._id
  const isConditionalSchedulingEnabled = booking?.isConditionalSchedulingEnabled
  const schedulingConditionSessionIndex =
    booking?.schedulingCondition?.sessionIndex

  // Calculate timezoneOffsetMinutes from timezone prop or use browser timezone
  const rescheduleSelectionTimezone = rescheduleSelection?.timezone

  // Calculate timezone string for proper DST handling
  const timezoneString = useMemo(() => {
    // First, try to use the timezone from props
    if (timezone) {
      return timezone
    }
    // Second, try to use timezone from rescheduleSelection
    if (rescheduleSelectionTimezone) {
      return rescheduleSelectionTimezone
    }
    // Third, try to use browser timezone
    let browserTimezone = ''
    try {
      if (
        typeof Intl !== 'undefined' &&
        Intl.DateTimeFormat &&
        Intl.DateTimeFormat().resolvedOptions
      ) {
        browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      }
    } catch (e) {
      browserTimezone = ''
    }
    return browserTimezone || null
  }, [timezone, rescheduleSelectionTimezone])

  const timezoneOffsetMinutes = useMemo(() => {
    // Use the timezone string to calculate offset
    if (timezoneString) {
      return getTimezoneOffsetMinutes(timezoneString)
    }
    // If no timezone is available, return null (will use browser local timezone in ScheduleTimeSlots)
    return null
  }, [timezoneString])

  useEffect(() => {
    const fetchMonthSlots = async () => {
      if (!coachId || !currentMonth) {
        setAvailableDays({})
        setTimeSlots([])
        setIsSlotsLoading(false)
        if (onDateChange) {
          onDateChange(null)
        }
        return
      }

      const normalizedMonth = moment(currentMonth).startOf('month')

      if (!normalizedMonth.isValid()) {
        setAvailableDays({})
        setTimeSlots([])
        setIsSlotsLoading(false)
        if (onDateChange) {
          onDateChange(null)
        }
        return
      }

      // Create cache key that includes sessionIndex if it exists (for conditional scheduling)
      // Format: "YYYY-MM" or "YYYY-MM-sessionIndex"
      let key = normalizedMonth.format('YYYY-MM')
      if (
        isConditionalSchedulingEnabled === true &&
        schedulingConditionSessionIndex
      ) {
        key = `${key}-${schedulingConditionSessionIndex}`
      }

      if (lastLoadedMonthKeyRef.current === key) {
        return
      }

      lastLoadedMonthKeyRef.current = key
      setIsSlotsLoading(true)

      const monthNumber = normalizedMonth.month() + 1
      const yearNumber = normalizedMonth.year()

      const payload = {
        month: Number(monthNumber),
        year: Number(yearNumber),
        eventId,
        isReschedule: true,
      }

      // Add sessionIndex if isConditionalSchedulingEnabled is true and schedulingCondition exists
      if (
        isConditionalSchedulingEnabled === true &&
        schedulingConditionSessionIndex
      ) {
        payload.sessionIndex = schedulingConditionSessionIndex
      }

      if (bookingDuration) {
        payload.sowSessionDuration = bookingDuration
      }
      if (bookingSowId) {
        payload.sowId = bookingSowId
      }
      // Determine clientTimezone:
      // 1) prefer explicit timezone prop
      // 2) if current user is a coach, fetch coach timezone from API (/coach/timezone)
      // 3) fallback to user's saved timezone or browser timezone
      // 4) validate against timezone list and finally default to 'Asia/Kolkata'
      let chosenTimezone = timezone || null

      if (!chosenTimezone && user?.isCoach) {
        try {
          const { data: tzData } = await API.CoachAPI.coachUser.getTimezone()
          if (tzData?.timezone) {
            chosenTimezone = tzData.timezone
          }
        } catch (err) {
          // ignore and fallback
        }
      }

      if (!chosenTimezone) {
        const userTimezone = user?.timezone
        let browserTimezone = ''
        try {
          if (
            typeof Intl !== 'undefined' &&
            Intl.DateTimeFormat &&
            Intl.DateTimeFormat().resolvedOptions
          ) {
            browserTimezone =
              Intl.DateTimeFormat().resolvedOptions().timeZone || ''
          }
        } catch (e) {
          browserTimezone = ''
        }
        chosenTimezone = userTimezone || browserTimezone || null
      }

      if (chosenTimezone) {
        const timezones = getSortedTimezones()
        const timezoneExists = timezones.some(
          (tz) => tz.value === chosenTimezone,
        )
        if (!timezoneExists) {
          chosenTimezone = 'Asia/Kolkata'
        }
      } else {
        chosenTimezone = 'Asia/Kolkata'
      }

      payload.clientTimezone = chosenTimezone

      try {
        const { data } = await API.ClientAPI.session.getAvailableSlots(
          coachId,
          payload,
        )

        const daysMap = (data?.days || []).reduce((acc, day) => {
          if (day?.date) {
            acc[day.date] = day
          }
          return acc
        }, {})

        setAvailableDays(daysMap)

        const nextSelectableDate = (() => {
          // When rescheduling, prefer the original booking date if it has slots
          // Otherwise, use first slot's date (user requirement)
          if (
            originalBookingDateISO &&
            daysMap[originalBookingDateISO]?.slots?.length
          ) {
            return originalBookingDateISO
          }

          // If there's a selected date from props and it has slots, use it
          if (selectedDateISO && daysMap[selectedDateISO]?.slots?.length) {
            return selectedDateISO
          }

          // Otherwise, find the first day with available slots
          const firstDayWithSlots = Object.values(daysMap).find(
            (day) => day?.slots?.length,
          )

          return firstDayWithSlots?.date || null
        })()

        if (onDateChange) {
          onDateChange(nextSelectableDate || null)
        }
      } catch (error) {
        if (Error) {
          Error.showError(error)
        }
        setAvailableDays({})
        setTimeSlots([])
        if (onDateChange) {
          onDateChange(null)
        }
      } finally {
        setIsSlotsLoading(false)
      }
    }

    fetchMonthSlots()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    coachId,
    currentMonth,
    bookingDuration,
    bookingSowId,
    selectedDateISO,
    onDateChange,
    timezone,
    originalBookingDateISO,
    eventId,
    isConditionalSchedulingEnabled,
    schedulingConditionSessionIndex,
  ])

  useEffect(() => {
    // Auto-select first available date if no date is selected or selected date has no slots
    if (
      (!selectedDateISO || !availableDays[selectedDateISO]?.slots?.length) &&
      Object.keys(availableDays).length > 0
    ) {
      const firstDayWithSlots = Object.values(availableDays).find(
        (day) => day?.slots?.length,
      )
      if (firstDayWithSlots?.date && onDateChange) {
        onDateChange(firstDayWithSlots.date)
        return
      }
    }

    if (selectedDateISO && availableDays[selectedDateISO]?.slots?.length) {
      setTimeSlots(availableDays[selectedDateISO].slots)
      return
    }
    setTimeSlots([])
  }, [selectedDateISO, availableDays, onDateChange])

  const availableSlots = useMemo(() => timeSlots || [], [timeSlots])

  const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  const validateGuestEmail = (email) => {
    if (!email || !email.trim()) {
      return null // Empty is allowed (will be filtered out)
    }
    if (!EMAIL_REGEX.test(email.trim())) {
      return 'Enter a valid email address'
    }
    return null
  }

  const validateAllGuests = () => {
    // Validate all non-empty guests in the list
    if (guestList && guestList.length > 0) {
      for (const guest of guestList) {
        const trimmed = guest?.trim()
        // Empty fields are allowed (they'll be filtered out on submit)
        if (trimmed && !EMAIL_REGEX.test(trimmed)) {
          return false
        }
      }
    }
    return true
  }

  const handleDateSelect = (dateISO) => {
    if (!onDateChange) return
    onDateChange(dateISO)
  }

  const handleSlotClickInternal = (slot) => {
    if (!selectedDateISO || isCheckingAvailability) {
      return
    }

    const payload = {
      date: selectedDateISO,
      slot,
      timezone: rescheduleSelection?.timezone,
    }

    if (onSlotSelect) {
      onSlotSelect(payload)
    }

    if (onSelectionComplete) {
      onSelectionComplete(payload)
    }
  }

  const clientName = booking?.clientName || '-'

  const durationLabel = booking?.durationLabel || '1h'

  const renderMetaPanel = () => (
    <div className='p-3 flex-md-shrink-0' style={{ minWidth: 300 }}>
      <h4 className='sa4 font-16px mb-3 font-weight-bold'>{clientName}</h4>
      <p className='mb-3'>
        <span className='sa6 text-uppercase'>Former time</span>
        <br />
        <del className='sa5 mt-1'>{formerDateLabel}</del>
        <br />
        <del className='sa5 mt-1'>{formerTimeLabel}</del>
      </p>
      <p className='mb-0'>
        <span className='sa6 text-uppercase'>Duration</span>
        <br />
        <span className='sa5 mt-1'>{durationLabel} Minutes</span>
      </p>
    </div>
  )

  const renderSelectionStep = () => {
    return (
      <div className='booking-reschedule-view mt-30px'>
        <div
          className='border border-dark d-flex flex-column flex-md-row rounded-left rounded-right overflow-hidden'
          style={{ minHeight: 490, height: 490 }}>
          {renderMetaPanel()}
          <div
            className='flex-fill p-3 border border-dark border-top-0 border-bottom-0 d-flex flex-column'
            style={{ minHeight: 0 }}>
            <ScheduleDatePicker
              currentMonth={currentMonth}
              selectedDate={selectedDateISO}
              onMonthChange={setCurrentMonth}
              onDateSelect={handleDateSelect}
              availableDays={availableDays}
              isLoading={isSlotsLoading}
              disableDatesWithoutSlots={true}
            />
          </div>
          <div
            className='flex-md-shrink-0 px-4 py-3 d-flex flex-column overflow-hidden'
            style={{ minHeight: 0, minWidth: 450 }}>
            <ScheduleTimeSlots
              slots={availableSlots}
              selectedSlot={rescheduleSelection?.slot}
              onSlotSelect={handleSlotClickInternal}
              isLoading={isSlotsLoading}
              timezoneOffsetMinutes={timezoneOffsetMinutes}
              timezone={timezoneString}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderDetailsStep = () => {
    const initialValues = {
      name: rescheduleForm?.name || '',
      email: rescheduleForm?.email || '',
      message: rescheduleForm?.message || '',
    }

    return (
      <div className='booking-reschedule-view mt-30px'>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={(values) => {
            // Validate all guests before submitting
            if (!validateAllGuests()) {
              return
            }

            if (onRescheduleFieldChange) {
              onRescheduleFieldChange('name', values.name)
              onRescheduleFieldChange('email', values.email)
              onRescheduleFieldChange('message', values.message)
            }
            if (onSubmit) {
              onSubmit()
            }
          }}>
          {({ values, setFieldValue, handleSubmit }) => (
            <>
              <div className='border border-dark d-flex flex-column flex-md-row'>
                {renderMetaPanel()}
                <div className='flex-fill p-0 border border-dark border-top-0 border-bottom-0 border-right-0'>
                  <div className='booking-reschedule-details h-100 border-0 p-3'>
                    <div className='row'>
                      <div className='col-12 mb-3 mb-md-0'>
                        {/* <div className='form-group mb-0'>
                          <InputField
                            name='name'
                            label='Your name'
                            placeholder='Enter your name'
                            showErrorMessage={false}
                            displayFlex={false}
                            inputClasses='input-typo text-dark-900'
                            labelProps={{
                              className: 'sa6 text-uppercase mb-2',
                            }}
                            onChange={(event) => {
                              setFieldValue('name', event.target.value)
                              if (onRescheduleFieldChange) {
                                onRescheduleFieldChange(
                                  'name',
                                  event.target.value,
                                )
                              }
                            }}
                          />
                        </div>

                        <div className='form-group mb-4'>
                          <InputField
                            name='email'
                            label='Email address'
                            placeholder='Enter email address'
                            showErrorMessage={false}
                            displayFlex={false}
                            inputClasses='input-typo text-dark-900'
                            labelProps={{
                              className: 'sa6 text-uppercase mb-2',
                            }}
                            onChange={(event) => {
                              setFieldValue('email', event.target.value)
                              if (onRescheduleFieldChange) {
                                onRescheduleFieldChange(
                                  'email',
                                  event.target.value,
                                )
                              }
                            }}
                          />
                        </div> */}

                        <div className='form-group mb-4'>
                          <p className='sa6 text-uppercase mb-2'>Add guests</p>

                          {/* Show all guest input fields */}
                          <div className='booking-reschedule-details__guest-list mb-2'>
                            {guestList && guestList.length > 0
                              ? guestList.map((guest, index) => {
                                  const emailError = validateGuestEmail(guest)
                                  const showError =
                                    guest && guest.trim() && emailError
                                  return (
                                    <div
                                      key={`guest-${index}`}
                                      className='mb-2'>
                                      <div className='booking-reschedule-details__guest-row'>
                                        <InputField
                                          name={`guest-${index}`}
                                          value={guest || ''}
                                          placeholder='Enter guest email'
                                          showErrorMessage={false}
                                          displayFlex={false}
                                          inputClasses={`input-typo text-dark-900 ${
                                            showError ? 'is-invalid' : ''
                                          }`}
                                          removeFormikField
                                          onChange={(event) => {
                                            const newEmail = event.target.value
                                            if (onGuestUpdate) {
                                              onGuestUpdate(index, newEmail)
                                            }
                                          }}
                                          formGroupProps={{
                                            className: 'mb-0 flex-fill',
                                          }}
                                        />
                                        <button
                                          type='button'
                                          className='booking-reschedule-details__guest-remove'
                                          onClick={() => {
                                            if (onRemoveGuest) {
                                              onRemoveGuest(index)
                                            }
                                          }}
                                          aria-label='Remove guest'>
                                          <DeleteIcon />
                                        </button>
                                      </div>
                                      {/* {showError && (
                                        <div className='text-danger s8 mt-1'>
                                          {emailError}
                                        </div>
                                      )} */}
                                    </div>
                                  )
                                })
                              : null}
                          </div>

                          {/* Add guest button - always creates a new input field */}
                          <div className='mt-2 booking-reschedule-details__guest-input d-flex justify-content-end align-items-center'>
                            <Button
                              variant='outline-dark'
                              className='mr-3 text-uppercase s8 py-2'
                              onClick={() => {
                                if (onAddGuest) {
                                  onAddGuest()
                                }
                              }}
                              disabled={isSubmitting}>
                              Add guest
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className='col-12'>
                        <div className='form-group mb-0'>
                          <InputField
                            name='message'
                            label='Reason for reschedule'
                            placeholder='Enter reason'
                            as='textarea'
                            rows={6}
                            showErrorMessage={false}
                            displayFlex={false}
                            inputClasses='input-typo text-dark-900 text-indent-none '
                            labelProps={{
                              className: 'sa6 text-uppercase mb-2',
                            }}
                            onChange={(event) => {
                              setFieldValue('message', event.target.value)
                              if (onRescheduleFieldChange) {
                                onRescheduleFieldChange(
                                  'message',
                                  event.target.value,
                                )
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='booking-reschedule-details__actions mt-3 d-flex justify-content-end'>
                <Button
                  variant='saffron'
                  form='question-editor-form'
                  className='save-btn-fixed-width mr-3'
                  onClick={handleSubmit}
                  disabled={isSubmitting}>
                  {isSubmitting ? 'Rescheduling...' : 'Reschedule'}
                </Button>
                <Button
                  variant='outline-dark'
                  className='text-uppercase s8'
                  onClick={onBackToList}
                  disabled={isSubmitting}>
                  Close
                </Button>
              </div>
            </>
          )}
        </Formik>
      </div>
    )
  }

  return (
    <div>
      <div className='d-flex align-items-center py-auto'>
        <Button
          variant='none'
          className='p-0 border-0 mr-3'
          onClick={onBackToList}>
          <svg
            width='20'
            height='32'
            viewBox='0 0 20 32'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M16 3L4 16'
              stroke='black'
              strokeWidth='3'
              strokeLinecap='round'
            />
            <path
              d='M16 29L4 16'
              stroke='black'
              strokeWidth='3'
              strokeLinecap='round'
            />
          </svg>
        </Button>
        <div>
          <h3 className='sa7 mb-1'>Re-scheduling</h3>
          {booking?.eventTitle && (
            <p className='sa2 mb-0'>{booking.eventTitle}</p>
          )}
        </div>
      </div>

      {rescheduleStep === RESCHEDULE_STEPS.SELECTION
        ? renderSelectionStep()
        : renderDetailsStep()}
    </div>
  )
}

export default ReschedulingView
